import {
    EmbedEvent, EmbedEvents, SessionCreatedEvent,
} from 'embed/types/events'
import {
    NinchatEventType, NinchatSessionCreatedEvent,
} from 'api/types/events'
import {NinchatProperties} from 'api/types'
import {RootState} from 'store'
import {composeQueueRequests} from 'embed/utils/metadata'
import {config} from 'embed/listeners'
import {createSession} from 'api/action/createSession'
import {describeTranscript} from 'api/action/describeTranscript'
import {extractProperties} from 'api/event'
import {getGroupQueues} from 'selectors/ninchat/groups'
import {getQueueActivity} from 'api/action/getQueueActivity'
import {getQueueIdsForTicketing} from 'hooks/queueTime/ticketDurationData'
import {getTicketDuration} from 'hooks/queueTime/useTicketDuration'
import {sessionCreated} from 'api/event/sessionCreated'
import {startAppListening} from 'middlewares/listener'

type Action = Parameters<Parameters<typeof startAppListening>[0]['effect']>[0]
type ListenerApi = Parameters<Parameters<typeof startAppListening>[0]['effect']>[1]

const isSessionCreatedEvent = (o: unknown): o is NinchatSessionCreatedEvent => !!(o && typeof o === 'object' && 'event' in o && o.event === NinchatEventType.NinchatSessionCreatedEvent)

const getSessionCreatedPayload = (payload: unknown): NinchatSessionCreatedEvent |
  undefined => {
    if (Array.isArray(payload)) {
        return payload.find(isSessionCreatedEvent)
    }
    return isSessionCreatedEvent(payload) ? payload : undefined
}

const initializeTicketingQueueStates = async (queues: NinchatSessionCreatedEvent['user_queues'], listenerApi: ListenerApi): Promise<void> => {
    if (!queues) {
        return
    }

    const initialState = listenerApi.getState()

    // Collect all queue IDs that need activity data for ticket duration calculation.
    const allQueueIdsForTicketing = Object.entries(queues).flatMap(([queueId, queue]) => {
        const isGroupQueue = !!queue.queue_attrs.is_group
        const groupQueues = getGroupQueues(initialState, isGroupQueue, queueId)
        const parentGroupQueue = queue.queue_attrs.group_id
            ? initialState.ninchat.user_queues?.[queue.queue_attrs.group_id] ?? initialState.ninchat._queues?.[queue.queue_attrs.group_id]
            : undefined

        return getQueueIdsForTicketing({
            groupQueues,
            parentGroupQueue,
            queue,
            queueId,
        })
    })

    const uniqueQueueIdsForTicketing = [...new Set(allQueueIdsForTicketing)]

    // Fetch queue activity for all relevant queues in parallel.
    if (uniqueQueueIdsForTicketing.length > 0) {
        await Promise.allSettled(
            uniqueQueueIdsForTicketing.map((queueId) => listenerApi.dispatch(getQueueActivity({queue_id: queueId}))),
        )

        // After activity is loaded, fetch transcripts for completed audiences that
        // have no transcript data yet. Transcript data directly reveals whether each
        // ticket is still unassigned, which is needed to compute the oldest pending time.
        const stateAfterActivity = listenerApi.getState()
        const existingTranscripts = {
            ...stateAfterActivity.ninchat._audience_transcripts,
            ...stateAfterActivity.ninchat._user_audience_transcripts,
        }

        const audiencesNeedingTranscripts = [
            ...new Set(
                uniqueQueueIdsForTicketing.flatMap((queueId) => (stateAfterActivity.ninchat._user_queues_activity[queueId] ?? [])
                    .filter((activity) => activity.event === 'audience_complete' && activity.audience_id)
                    .map((activity) => activity.audience_id as NinchatProperties.AudienceID)
                    .filter((audienceId) => !existingTranscripts[audienceId])),
            ),
        ]

        if (audiencesNeedingTranscripts.length > 0) {
            await Promise.allSettled(
                audiencesNeedingTranscripts.map((audienceId) => listenerApi.dispatch(describeTranscript({audience_id: audienceId}))),
            )
        }
    }
}

export const composeSessionCreatedQueues = (
    userQueues: NinchatSessionCreatedEvent['user_queues'],
    state: RootState,
    targetOrigin: string,
): SessionCreatedEvent['user_queues'] => (userQueues
    ? Object.fromEntries(
        Object.entries(userQueues).map(([queueId, queue]) => {
            const {
                delivery_time, queue_requests, queue_time, realm_id, queue_attrs: {
                    length, name, group_id, is_group, unassigned_registered,
                },
            } = queue

            const isGroupQueue = !!is_group
            const groupQueues = getGroupQueues(state, isGroupQueue, queueId)
            const parentGroupQueue = group_id
                ? state.ninchat.user_queues?.[group_id] ?? state.ninchat._queues?.[group_id]
                : undefined

            const ticketDuration = getTicketDuration({
                audienceComments: state.ninchat._user_audience_comments,
                audienceTranscripts: {
                    ...state.ninchat._audience_transcripts,
                    ...state.ninchat._user_audience_transcripts,
                },
                groupQueues,
                parentGroupQueue,
                queue,
                queueActivities: state.ninchat._user_queues_activity,
                queueId,
            })

            return [queueId, {
                delivery_time,
                queue_attrs: {
                    group_id,
                    is_group,
                    length,
                    name,
                    unassigned_registered,
                },
                ...queue_requests && {queue_requests: composeQueueRequests(queue_requests, targetOrigin)},
                queue_time,
                realm_id,
                ...ticketDuration.queue_time && {unassigned_registered_time: ticketDuration.queue_time},
            }]
        }),
    )
    : undefined)

export const sessionCreatedListener = (sendToParent: (event: EmbedEvents) => void) => ({
    effect: async (action: Action, listenerApi: ListenerApi) => {
        const targetOrigin = config.get('targetOrigin')

        if (!targetOrigin) {
            console.error('missing targetOrigin')
            return
        }

        const payload = getSessionCreatedPayload(action.payload)

        if (!payload) {
            return
        }

        const {
            user_channels,
            user_queues,
        } =
               extractProperties<NinchatSessionCreatedEvent>(payload)

        await initializeTicketingQueueStates(user_queues, listenerApi)

        const state = listenerApi.getState()

        sendToParent({
            event: EmbedEvent.SessionCreated,
            targetOrigin,
            user_channels: user_channels ?
                Object.fromEntries(
                    Object.entries(user_channels)
                        .filter(([, channel]) => channel.channel_attrs?.audience_id)
                        .map(([channelId, channel]) => {
                            const {
                                channel_attrs: {
                                    audience_id, queue_id, name, closed,
                                },
                                channel_status,
                                realm_id,
                            } = channel
                            return [channelId, {
                                channel_attrs: {
                                    audience_id,
                                    closed,
                                    name,
                                    queue_id,
                                },
                                channel_status,
                                realm_id,
                            }]
                        }),
                )
                :
                undefined,
            user_queues: composeSessionCreatedQueues(user_queues, state, targetOrigin),
        })
    },
    predicate: (action: Action) => action?.type === createSession.fulfilled.type ||
           action?.type === sessionCreated.fulfilled.type,
})

export const start = (sendToParent: (event: EmbedEvents) => void) => [
    startAppListening(sessionCreatedListener(sendToParent)),
]
